// Sugalarm — glucose math + formatting helpers. Ported from the design
// bundle's data.jsx, retyped for TypeScript.

import {
  type Thresholds,
  type Trend,
  type Zone,
} from "./types";

// Cycle 35: `t` is required. Every caller must thread live thresholds
// (via `useThresholds()` at screen scope or a prop) — the old default
// silently masked a per-user-threshold bug where the hero / pill / dot
// colors disagreed with the alarm banner.
export function zoneOf(v: number | null | undefined, t: Thresholds): Zone {
  if (v == null) return "unknown";
  if (v <= t.urgent_low) return "urgent_low";
  if (v < t.target_low) return "low";
  if (v <= t.target_high) return "in_range";
  if (v < t.urgent_high) return "high";
  return "urgent_high";
}

export function zoneColor(zone: Zone): string {
  switch (zone) {
    case "urgent_low":
    case "urgent_high":
      return "var(--zone-urgent)";
    case "low":
    case "high":
      return "var(--zone-warn)";
    case "in_range":
      return "var(--zone-ok)";
    default:
      return "var(--ink-3)";
  }
}

export function zoneSoft(zone: Zone): string {
  switch (zone) {
    case "urgent_low":
    case "urgent_high":
      return "var(--zone-urgent-soft)";
    case "low":
    case "high":
      return "var(--zone-warn-soft)";
    case "in_range":
      return "var(--zone-ok-soft)";
    default:
      return "var(--zone-neutral-soft)";
  }
}

export function zoneTone(zone: Zone): "urgent" | "warn" | "ok" | "neutral" {
  if (zone === "urgent_low" || zone === "urgent_high") return "urgent";
  if (zone === "low" || zone === "high") return "warn";
  if (zone === "in_range") return "ok";
  return "neutral";
}

export function zoneLabel(zone: Zone): string {
  return (
    {
      urgent_low: "Urgent low",
      low: "Low",
      in_range: "In range",
      high: "High",
      urgent_high: "Urgent high",
      unknown: "—",
    } as const
  )[zone];
}

// Trend lookup tables. The wire-level `trend` string is canonical when
// present; rate-of-change is the fallback.
const TREND_TABLE: Record<Trend, { glyph: string; label: string; short: string; rate: number }> = {
  DoubleUp:      { glyph: "↑↑", label: "Rising fast",     short: "rising fast", rate: 3.5 },
  SingleUp:      { glyph: "↑",  label: "Rising",          short: "rising",      rate: 2.5 },
  FortyFiveUp:   { glyph: "↗",  label: "Rising slowly",   short: "rising slow", rate: 1.5 },
  Flat:          { glyph: "→",  label: "Steady",          short: "steady",      rate: 0   },
  FortyFiveDown: { glyph: "↘",  label: "Falling slowly",  short: "falling slow",rate: -1.5 },
  SingleDown:    { glyph: "↓",  label: "Falling",         short: "falling",     rate: -2.5 },
  DoubleDown:    { glyph: "↓↓", label: "Falling fast",    short: "falling fast",rate: -3.5 },
};

export interface TrendInfo {
  glyph: string;
  label: string;
  short: string;
  rate: number;
}

export function trendFromRate(r: number): TrendInfo {
  if (r >= 3) return { glyph: "↑↑", label: "Rising fast",   short: "rising fast", rate: r };
  if (r >= 2) return { glyph: "↑",  label: "Rising",        short: "rising",      rate: r };
  if (r >= 1) return { glyph: "↗",  label: "Rising slowly", short: "rising slow", rate: r };
  if (r > -1) return { glyph: "→",  label: "Steady",        short: "steady",      rate: r };
  if (r > -2) return { glyph: "↘",  label: "Falling slowly",short: "falling slow",rate: r };
  if (r > -3) return { glyph: "↓",  label: "Falling",       short: "falling",     rate: r };
  return       { glyph: "↓↓", label: "Falling fast",  short: "falling fast",rate: r };
}

export function trendInfo(
  trend: Trend | null,
  rate: number | null,
): TrendInfo {
  if (trend && trend in TREND_TABLE) return TREND_TABLE[trend];
  return trendFromRate(rate ?? 0);
}

// ─── Time formatting ───────────────────────────────────────────────────

export function fmtTime(t: number | string | Date, opts: { h24?: boolean } = {}): string {
  const d = t instanceof Date ? t : new Date(t);
  const h = d.getHours();
  const m = d.getMinutes();
  if (opts.h24) {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const ampm = h >= 12 ? "pm" : "am";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/** Whole minutes of age, floored — a LABEL helper (`fmtAge`), never a
 *  threshold input. ⚠ Cycle 435: the flooring here was being reused as the
 *  staleness comparison (`ageMinutes > threshold`), which let a value
 *  10:59 old pass as fresh against a server comparing exact seconds
 *  (`glucose_service.age_is_stale`). Every threshold comparison now goes
 *  through `ageSeconds` / `sampleIsStale`; this stays floored because
 *  "10 min ago" is what a label says. */
export function ageMinutes(t: number | string | Date, now: number = Date.now()): number {
  return Math.floor(ageSeconds(t, now) / 60);
}

/** Exact age in seconds (never negative — a clock that ran backwards reads
 *  as fresh, exactly as `ageMinutes` always did). The ONE age every
 *  freshness comparison is made in. */
export function ageSeconds(t: number | string | Date, now: number = Date.now()): number {
  const ts = typeof t === "number" ? t : new Date(t).getTime();
  if (!Number.isFinite(ts)) return Number.POSITIVE_INFINITY;
  return Math.max(0, (now - ts) / 1000);
}

export function fmtAge(min: number): string {
  if (min < 1) return "just now";
  if (min === 1) return "1 min ago";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  if (h < 24) return `${h}h ${rem}m ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Absolute timestamp formatter for log-entry strips (cycle 29).
 *  Same day → `fmtTime(timestamp)` (respects h24).
 *  Within the last 6 days → short weekday + time (e.g. `Mon 2:14 PM`).
 *  Older → short month + day + time (e.g. `May 2 2:14 PM`). */
export function fmtEntryTime(
  timestamp: number | string | Date,
  now: number | Date = Date.now(),
  h24 = false,
): string {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  const n = now instanceof Date ? now : new Date(now);
  const sameDay =
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate();
  const time = fmtTime(d, { h24 });
  if (sameDay) return time;
  // Day-difference based on calendar midnights, not raw ms — so an entry
  // logged at 11pm yesterday reads "Mon", not "today" off a 23h delta.
  const dMid = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const nMid = new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime();
  const days = Math.round((nMid - dMid) / 86_400_000);
  if (days >= 1 && days <= 6) {
    const weekday = new Intl.DateTimeFormat(undefined, {
      weekday: "short",
    }).format(d);
    return `${weekday} ${time}`;
  }
  const monthDay = new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
  }).format(d);
  return `${monthDay} ${time}`;
}

/** Local-TZ `YYYY-MM-DD` key. `en-CA` formats as ISO date natively so
 *  the key sorts lexicographically (== chronologically). UTC + 11pm
 *  logging is a real case — bucket by local calendar day, not UTC.
 *  (Cycle 47: relocated here from the now-deleted calendar hook;
 *  shared by `LogScreen`'s kiosk today-filter and the Recent Entries
 *  day-picker.) */
export function localDayKey(ts: number | string | Date): string {
  const d = ts instanceof Date ? ts : new Date(ts);
  return d.toLocaleDateString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/** Provider-aware staleness, mirroring the server's authoritative
 *  thresholds in `server/services/glucose_service.py`
 *  (`_STALENESS_THRESHOLDS` / `_STALENESS_DEFAULT`). Dexcom samples
 *  every ~5 min, Libre every ~15 min, so a flat client-side 15-min
 *  rule wrongly flagged a perfectly fresh Libre reading (16–19 min
 *  old) as stale — the Now screen then showed "No Data" while the
 *  server (and every other client surface that trusts the server's
 *  `stale` flag) still considered the member live. Keep this table in
 *  sync with the server if the server's ever changes.
 *
 *  `STALE_MINUTES` stays exported as the provider-unknown default for
 *  backward compatibility with any caller that omits the provider. */
export const STALE_MINUTES = 15;
const STALE_MINUTES_BY_PROVIDER: Record<string, number> = {
  dexcom: 10,
  // Cycle 234.5: Libre tuned to Dexcom parity (was 20). The adapter now
  // stores LLU's live `glucoseMeasurement` every poll instead of the
  // ~15m-lagging `graphData`, so Libre delivers ~5m-fresh readings and the
  // 20m padding (which read a 19m-old value as live) is no longer needed.
  // Mirrors the server's _STALENESS_THRESHOLDS.
  libre: 10,
  // Cycle 469: the urgent-low drill labels the readings it writes `drill`;
  // the server judges them by Dexcom's line and so does the SPA.
  drill: 10,
};

export function staleMinutesFor(provider?: string | null): number {
  if (!provider) return STALE_MINUTES;
  return STALE_MINUTES_BY_PROVIDER[provider.toLowerCase()] ?? STALE_MINUTES;
}

/** The provider rule in SECONDS — the unit the server compares in. */
export function staleSecondsFor(provider?: string | null): number {
  return staleMinutesFor(provider) * 60;
}

/** ⚠ Cycle 435: compared in SECONDS, exactly as the server does
 *  (`glucose_service.age_is_stale`: `age_seconds > threshold`). Until this
 *  cycle the age was floored to whole minutes first, so a reading 10:59
 *  old read `10 > 10 = false` here while the server had called it stale
 *  59 seconds earlier — the client was LOOSER than the server by up to a
 *  minute, on every surface that mirrors the rule. 10:00 is fresh, 10:01
 *  is stale, on both sides. */
export function isStale(
  t: number | string | Date | null,
  now: number = Date.now(),
  provider?: string | null,
): boolean {
  if (t == null) return true;
  return ageSeconds(t, now) > staleSecondsFor(provider);
}

/** Cycle 435: the signed mg/dL delta between the last two readings of a
 *  chronological (oldest-first) history buffer, or null. History maths in
 *  the lib, so no presentation module reads `.sgv` off a raw `Reading`
 *  (the AST guard, `__tests__/currentGlucoseGuard.test.ts`). The label it
 *  feeds is painted only beside a CURRENT number. */
export function recentDeltaMgdl(recent: readonly { sgv: number }[]): number | null {
  if (recent.length < 2) return null;
  return recent[recent.length - 1]!.sgv - recent[recent.length - 2]!.sgv;
}

/** Convert mg/dL to mmol/L for display (the wire stays mg/dL). */
export const MGDL_PER_MMOL = 18.0182;
export function toMmol(mgdl: number): number {
  return Math.round((mgdl / MGDL_PER_MMOL) * 10) / 10;
}

export function displayValue(
  sgv: number,
  units: "mg/dL" | "mmol/L" = "mg/dL",
): string {
  if (units === "mmol/L") return toMmol(sgv).toFixed(1);
  return String(sgv);
}

// Cycle 309: `statusSpeechText` (cycle 9/16) and its zone/trend phrasing
// helpers were RETIRED — every spoken glucose sentence now composes
// through `lib/utterance.ts` (server-authoritative classification +
// veracity gate + the humanized copy contract). Nothing here may map a
// glucose value to a SPOKEN zone again; `zoneOf` below remains the
// DISPLAY-side resolver (colors, pills, charts) only.
